import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Text
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'

import { useNotifications } from '../../contexts/NotificationContext'
import { INotification } from '../../services/Notification/interfaces'
import { Icon } from '../Icon'

function formatTimeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = Math.max(0, now.getTime() - date.getTime())

  const minutes = Math.floor(diffMs / 60000)
  const hours = Math.floor(diffMs / 3600000)
  const days = Math.floor(diffMs / 86400000)

  if (minutes < 1) return 'Agora'
  if (minutes < 60) return `${minutes}min`
  if (hours < 24) return `${hours}h`
  if (days < 30) return `${days}d`
  return `${Math.floor(days / 30)}m`
}

function sanitizeIconClass(icon: string): string {
  return icon.replace(/[^a-zA-Z0-9-]/g, '')
}

function isValidLink(link: string, linkType: 'internal' | 'external'): boolean {
  if (linkType === 'internal') return link.startsWith('/')
  return /^https?:\/\//i.test(link)
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead } = useNotifications()
  const navigate = useNavigate()

  const handleClick = async (notification: INotification) => {
    if (!notification.is_read) {
      try {
        await markAsRead(notification.id)
      } catch {
        // Falha ao marcar como lida não bloqueia navegação
      }
    }

    if (!isValidLink(notification.link, notification.link_type)) return

    if (notification.link_type === 'external') {
      window.open(notification.link, '_blank', 'noopener,noreferrer')
    } else {
      navigate(notification.link)
    }
  }

  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount)

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Button variant="ghost" size="sm" ml={3} position="relative" px={2} aria-label="Notificações">
          <Icon name="lni lni-alarm" size="lg" />
          {unreadCount > 0 && (
            <Badge
              colorScheme="red"
              position="absolute"
              top="-2px"
              right="-2px"
              minW="18px"
              h="18px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {badgeText}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent w="360px" maxH="450px">
        <PopoverHeader fontWeight="bold" fontSize="sm">
          Notificações
        </PopoverHeader>
        <PopoverBody p={0} overflowY="auto" maxH="340px">
          {notifications.length === 0 ? (
            <Text variant="caption" p={4} textAlign="center">
              Nenhuma notificação
            </Text>
          ) : (
            notifications.map((notification, index) => (
              <Box key={notification.id}>
                <Flex
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: 'gray.100' }}
                  bg={notification.is_read ? 'transparent' : 'blue.50'}
                  onClick={() => handleClick(notification)}
                  alignItems="flex-start"
                  gap={3}
                >
                  <Box flexShrink={0} mt={1}>
                    <Icon name={`lni lni-${sanitizeIconClass(notification.icon)}`} size="lg" />
                  </Box>
                  <Box flex={1} minW={0}>
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text fontSize="sm" fontWeight={notification.is_read ? 'normal' : 'bold'} noOfLines={1}>
                        {notification.title}
                      </Text>
                      <Text variant="caption" flexShrink={0} ml={2}>
                        {formatTimeAgo(notification.created_at)}
                      </Text>
                    </Flex>
                    <Text variant="caption" noOfLines={2}>
                      {notification.description}
                    </Text>
                  </Box>
                </Flex>
                {index < notifications.length - 1 && <Divider m={0} />}
              </Box>
            ))
          )}
        </PopoverBody>
        <PopoverFooter textAlign="center" p={2}>
          <Button variant="link" size="sm" colorScheme="blue" onClick={() => navigate('/notificacoes')}>
            Ver todas notificações →
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  )
}
